/**
 * Document Search Service for RAG (Retrieval-Augmented Generation)
 *
 * Provides document search and context-building for the AI chatbot.
 * Uses Supabase queries (ilike, metadata matching) to find relevant documents,
 * then assembles truncated text excerpts to fit within the LLM context window.
 */

import { supabase } from '@/lib/supabase'
import { profileService } from './profileService'
import type { Document } from '@/types/database'

// -------------------------------------------------------------------
// Types
// -------------------------------------------------------------------

export interface DocumentSearchResult {
  id: string
  name: string
  type: string | null
  summary: string | null
  textExcerpt: string | null
  tags: string[] | null
  relatedStandards: string[] | null
  issuer: string | null
  expiryDate: string | null
  issuedDate: string | null
  productId: string | null
  createdAt: string | null
  /** Relevance score (higher = more relevant) */
  score: number
}

export interface DocumentContext {
  documents: DocumentSearchResult[]
  totalFound: number
  /** Combined text for the LLM prompt */
  contextText: string
  /** Approximate token count of contextText */
  estimatedTokens: number
}

export interface SearchOptions {
  /** Maximum number of results to return */
  maxResults?: number
  /** Filter by document type */
  documentType?: string
  /** Filter by product ID */
  productId?: string
  /** Maximum tokens for combined context */
  maxTokens?: number
}

// -------------------------------------------------------------------
// Constants
// -------------------------------------------------------------------

/** Default maximum tokens for document context in the prompt */
const DEFAULT_MAX_TOKENS = 6000

/** Rough chars-per-token estimate for English text */
const CHARS_PER_TOKEN = 4

/** Maximum results to consider before ranking */
const MAX_CANDIDATES = 30

/** Maximum results to include in final context */
const DEFAULT_MAX_RESULTS = 5

// -------------------------------------------------------------------
// Private helpers
// -------------------------------------------------------------------

/**
 * Extract keywords from a query string for matching.
 * Removes common stop words and returns unique terms.
 */
function extractKeywords(query: string): string[] {
  const stopWords = new Set([
    'a', 'an', 'the', 'is', 'are', 'was', 'were', 'be', 'been', 'being',
    'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could',
    'should', 'may', 'might', 'can', 'shall', 'to', 'of', 'in', 'for',
    'on', 'with', 'at', 'by', 'from', 'as', 'into', 'through', 'during',
    'before', 'after', 'above', 'below', 'between', 'out', 'off', 'over',
    'under', 'again', 'further', 'then', 'once', 'here', 'there', 'when',
    'where', 'why', 'how', 'all', 'each', 'every', 'both', 'few', 'more',
    'most', 'other', 'some', 'such', 'no', 'nor', 'not', 'only', 'own',
    'same', 'so', 'than', 'too', 'very', 'just', 'because', 'but', 'and',
    'or', 'if', 'while', 'about', 'what', 'which', 'who', 'whom', 'this',
    'that', 'these', 'those', 'am', 'it', 'its', 'i', 'me', 'my', 'we',
    'our', 'you', 'your', 'he', 'him', 'his', 'she', 'her', 'they', 'them',
    'their', 'need', 'get', 'show', 'tell', 'give', 'any', 'much',
  ])

  return query
    .toLowerCase()
    .replace(/[^\w\s-]/g, ' ')
    .split(/\s+/)
    .filter(word => word.length > 1 && !stopWords.has(word))
    .filter((word, index, arr) => arr.indexOf(word) === index)
}

/**
 * Score a document based on how well it matches the query keywords.
 */
function scoreDocument(doc: Document, keywords: string[]): number {
  if (keywords.length === 0) return 0

  let score = 0
  const lowerName = (doc.name || '').toLowerCase()
  const lowerDesc = (doc.description || '').toLowerCase()
  const lowerSummary = (doc.ai_summary || '').toLowerCase()
  const lowerContent = (doc.text_content || '').toLowerCase()
  const tags = (doc.tags || []).map(t => t.toLowerCase())
  const standards = (doc.related_standards || []).map(s => s.toLowerCase())

  for (const keyword of keywords) {
    // Name matches are highest value
    if (lowerName.includes(keyword)) score += 10

    // Tag matches are very valuable
    if (tags.some(t => t.includes(keyword))) score += 8

    // Standard matches are very valuable
    if (standards.some(s => s.includes(keyword))) score += 8

    // Summary matches are good
    if (lowerSummary.includes(keyword)) score += 5

    // Description matches
    if (lowerDesc.includes(keyword)) score += 4

    // Text content matches (has the actual content)
    if (lowerContent.includes(keyword)) score += 3

    // Issuer match
    if ((doc.issuer || '').toLowerCase().includes(keyword)) score += 4
  }

  // Bonus for having text content (makes the document more useful for RAG)
  if (doc.text_content && doc.text_content.length > 50) {
    score += 2
  }

  // Bonus for having a summary
  if (doc.ai_summary && doc.ai_summary.length > 10) {
    score += 1
  }

  return score
}

/**
 * Truncate text to fit within a character limit, trying to break at sentence boundaries.
 */
function truncateText(text: string, maxChars: number): string {
  if (text.length <= maxChars) return text

  // Try to truncate at a sentence boundary
  const truncated = text.substring(0, maxChars)
  const lastPeriod = truncated.lastIndexOf('.')
  const lastNewline = truncated.lastIndexOf('\n')
  const breakPoint = Math.max(lastPeriod, lastNewline)

  if (breakPoint > maxChars * 0.5) {
    return truncated.substring(0, breakPoint + 1) + ' [...]'
  }

  return truncated + ' [...]'
}

/**
 * Format a document into a context block for the LLM prompt.
 */
function formatDocumentForContext(doc: DocumentSearchResult, index: number): string {
  const parts: string[] = []
  parts.push(`### Document ${index + 1}: ${doc.name}`)

  if (doc.type) parts.push(`Type: ${doc.type}`)
  if (doc.issuer) parts.push(`Issuer: ${doc.issuer}`)
  if (doc.issuedDate) parts.push(`Issued: ${doc.issuedDate}`)
  if (doc.expiryDate) parts.push(`Expires: ${doc.expiryDate}`)
  if (doc.tags && doc.tags.length > 0) parts.push(`Tags: ${doc.tags.join(', ')}`)
  if (doc.relatedStandards && doc.relatedStandards.length > 0) {
    parts.push(`Standards: ${doc.relatedStandards.join(', ')}`)
  }
  if (doc.summary) parts.push(`Summary: ${doc.summary}`)
  if (doc.textExcerpt) parts.push(`Content:\n${doc.textExcerpt}`)

  return parts.join('\n')
}

// -------------------------------------------------------------------
// Public API
// -------------------------------------------------------------------

export const documentSearchService = {
  /**
   * Search documents by query string with metadata matching.
   * Returns ranked results based on keyword relevance.
   */
  async searchDocuments(
    query: string,
    options: SearchOptions = {}
  ): Promise<DocumentSearchResult[]> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) return []

    const maxResults = options.maxResults ?? DEFAULT_MAX_RESULTS
    const keywords = extractKeywords(query)

    // Build the Supabase query — fetch candidate documents
    let dbQuery = supabase
      .from('documents')
      .select('id, name, type, description, ai_summary, text_content, tags, related_standards, issuer, expiry_date, issued_date, product_id, created_at')
      .eq('organization_id', orgId)
      .limit(MAX_CANDIDATES)

    // Apply optional filters
    if (options.documentType) {
      dbQuery = dbQuery.eq('type', options.documentType)
    }
    if (options.productId) {
      dbQuery = dbQuery.eq('product_id', options.productId)
    }

    const { data: documents, error } = await dbQuery

    if (error) {
      console.error('Document search error:', error)
      return []
    }

    if (!documents || documents.length === 0) return []

    // Score and rank documents
    const scored = documents
      .map(doc => ({
        doc,
        score: scoreDocument(doc as Document, keywords),
      }))
      .filter(item => item.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, maxResults)

    // Build results with truncated text excerpts
    const maxExcerptChars = Math.floor(
      ((options.maxTokens ?? DEFAULT_MAX_TOKENS) * CHARS_PER_TOKEN) / maxResults
    )

    return scored.map(({ doc, score }) => ({
      id: doc.id,
      name: doc.name,
      type: doc.type,
      summary: doc.ai_summary,
      textExcerpt: doc.text_content
        ? truncateText(doc.text_content, maxExcerptChars)
        : null,
      tags: doc.tags,
      relatedStandards: doc.related_standards,
      issuer: doc.issuer,
      expiryDate: doc.expiry_date,
      issuedDate: doc.issued_date,
      productId: doc.product_id,
      createdAt: doc.created_at,
      score,
    }))
  },

  /**
   * Fetch full text_content for specific document IDs.
   */
  async getDocumentContext(documentIds: string[]): Promise<Map<string, string>> {
    if (documentIds.length === 0) return new Map()

    const orgId = await profileService.getOrganizationId()
    if (!orgId) return new Map()

    const { data, error } = await supabase
      .from('documents')
      .select('id, text_content')
      .eq('organization_id', orgId)
      .in('id', documentIds)
      .not('text_content', 'is', null)

    if (error || !data) return new Map()

    const result = new Map<string, string>()
    for (const doc of data) {
      if (doc.text_content) {
        result.set(doc.id, doc.text_content)
      }
    }
    return result
  },

  /**
   * Build a RAG context string from documents matching the query.
   * This is the main entry point called by the AI assistant service.
   *
   * Returns a formatted context block + metadata about which documents were found.
   */
  async buildRAGContext(
    query: string,
    maxTokens: number = DEFAULT_MAX_TOKENS
  ): Promise<DocumentContext> {
    const results = await this.searchDocuments(query, { maxTokens })

    if (results.length === 0) {
      return {
        documents: [],
        totalFound: 0,
        contextText: '',
        estimatedTokens: 0,
      }
    }

    // Build the context text, respecting token limits
    const maxChars = maxTokens * CHARS_PER_TOKEN
    const contextParts: string[] = []
    let totalChars = 0

    for (let i = 0; i < results.length; i++) {
      const docBlock = formatDocumentForContext(results[i], i)
      if (totalChars + docBlock.length > maxChars) {
        // Truncate this block to fit remaining space
        const remaining = maxChars - totalChars
        if (remaining > 200) {
          contextParts.push(truncateText(docBlock, remaining))
        }
        break
      }
      contextParts.push(docBlock)
      totalChars += docBlock.length
    }

    const contextText = contextParts.join('\n\n')

    return {
      documents: results,
      totalFound: results.length,
      contextText,
      estimatedTokens: Math.ceil(contextText.length / CHARS_PER_TOKEN),
    }
  },
}
